import React, { useState, useEffect, useRef, useCallback } from 'react';
import MainLayout from '../../components/MainLayout';
import { fetchEquipments, fetchMachineAlarm, fetchShotRawData } from '../../services/api';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { useAlarmNotification } from '../../context/AlarmNotificationContext';
import { useTranslation } from '../../context/LanguageContext';
import './EquipmentBoard.css';

const SPLIT_OPTIONS = [
  { value: 1, icon: 'crop_square', labelKey: 'pages.monitoring.equipmentBoard.split1' },
  { value: 2, icon: 'splitscreen', labelKey: 'pages.monitoring.equipmentBoard.split2' },
  { value: 4, icon: 'grid_view', labelKey: 'pages.monitoring.equipmentBoard.split4' },
];

const SHOT_COLUMNS = [
  ['shotTime', 'pages.monitoring.equipmentBoard.cols.shotTime'],
  ['craftId', 'pages.monitoring.equipmentBoard.cols.craftId'],
  ['shotCount', 'pages.monitoring.equipmentBoard.cols.shotCount'],
  ['cycleTime', 'pages.monitoring.equipmentBoard.cols.cycleTime'],
  ['injTime', 'pages.monitoring.equipmentBoard.cols.injTime'],
  ['holdTime', 'pages.monitoring.equipmentBoard.cols.holdTime'],
  ['chargeTime', 'pages.monitoring.equipmentBoard.cols.chargeTime'],
  ['moldOpenTime', 'pages.monitoring.equipmentBoard.cols.moldOpenTime'],
  ['moldCloseTime', 'pages.monitoring.equipmentBoard.cols.moldCloseTime'],
  ['fetchTime', 'pages.monitoring.equipmentBoard.cols.fetchTime'],
  ['ejectTime', 'pages.monitoring.equipmentBoard.cols.ejectTime'],
];

function EquipmentPanel({ index, machines, splitCount, onNewAlarm, onSelectedIdChange, panelAlarms, onRemovePanelAlarm, onDataLoaded, lastUpdated, updateInfoRef }) {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState(() => {
    // localStorage에서 저장된 선택 복원
    try {
      const saved = JSON.parse(localStorage.getItem('eq-board-panels') || '{}');
      return saved[index] || null;
    } catch { return null; }
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [alarmData, setAlarmData] = useState(null);
  const [shotRows, setShotRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newShotIds, setNewShotIds] = useState(new Set());
  const prevMaxShotRef = useRef(null);
  const prevAlarmCountRef = useRef(null);
  const dropdownRef = useRef(null);

  // 첫 로드 시 저장된 값 없으면 machines[index] 자동 선택
  useEffect(() => {
    if (machines.length > 0 && !selectedId) {
      const idx = Math.min(index, machines.length - 1);
      setSelectedId(machines[idx]?.id || null);
    }
  }, [machines]);

  // 설비 변경 시 리셋 + 부모에 알림
  useEffect(() => {
    prevMaxShotRef.current = null;
    prevAlarmCountRef.current = null;
    isFirstLoadRef.current = true;
    setNewShotIds(new Set());
    if (onSelectedIdChange) onSelectedIdChange(index, selectedId);
  }, [selectedId]);

  const isFirstLoadRef = useRef(true);
  const fetchData = useCallback(() => {
    if (!selectedId) return;
    if (isFirstLoadRef.current) setLoading(true);
    Promise.all([
      fetchMachineAlarm(selectedId).catch(() => null),
      fetchShotRawData(selectedId, 50).catch(() => null),
    ])
      .then(([alarm, shot]) => {
        // 알람 처리
        const newAlarms = alarm?.alarmHistory || [];
        if (prevAlarmCountRef.current !== null && newAlarms.length > prevAlarmCountRef.current) {
          const addedCount = newAlarms.length - prevAlarmCountRef.current;
          const addedAlarms = newAlarms.slice(0, addedCount);
          const machine = machines.find(m => m.id === selectedId);
          onNewAlarm(addedAlarms, machine?.equipmentName || `#${selectedId}`);
        }
        prevAlarmCountRef.current = newAlarms.length;
        setAlarmData(alarm);

        // 샷 데이터 처리 + 새 샷 감지
        const rows = shot?.rows || [];
        if (rows.length > 0) {
          const maxShot = Math.max(...rows.map(r => r.shotCount || 0));
          if (prevMaxShotRef.current !== null && maxShot > prevMaxShotRef.current) {
            const ids = new Set();
            rows.forEach(r => {
              if (r.shotCount > prevMaxShotRef.current) ids.add(r.shotCount);
            });
            setNewShotIds(ids);
            setTimeout(() => setNewShotIds(new Set()), 5000);
          }
          prevMaxShotRef.current = maxShot;
        }
        setShotRows(rows);
      })
      .finally(() => { setLoading(false); isFirstLoadRef.current = false; if (onDataLoaded) onDataLoaded(); });
  }, [selectedId, machines, onNewAlarm]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useAutoRefresh(fetchData, !!selectedId);

  // 드롭다운 외부 클릭
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isDropdownOpen]);

  const selectedMachine = machines.find(m => m.id === selectedId);
  const alarms = alarmData?.alarmHistory || [];

  const handleSelect = (id) => {
    setSelectedId(id);
    setIsDropdownOpen(false);
  };

  const fmtVal = (v) => {
    if (v == null) return '-';
    if (typeof v === 'number') return v.toLocaleString();
    return String(v);
  };

  const fmtTime = (t) => {
    if (!t) return '-';
    const idx = t.indexOf('T');
    if (idx < 0) return t;
    const date = t.substring(5, 10).replace('-', '/');
    const time = t.substring(idx + 1, idx + 9);
    return `${date} ${time}`;
  };

  const getSeverityStyle = (severity) => {
    switch (severity) {
      case 'CRITICAL':
        return { bg: 'rgba(239, 68, 68, 0.2)', border: 'rgba(239, 68, 68, 0.3)', text: '#f87171', icon: 'error' };
      case 'WARNING':
        return { bg: 'rgba(251, 191, 36, 0.2)', border: 'rgba(251, 191, 36, 0.3)', text: '#fbbf24', icon: 'warning' };
      case 'INFO':
        return { bg: 'rgba(59, 130, 246, 0.2)', border: 'rgba(59, 130, 246, 0.3)', text: '#60a5fa', icon: 'info' };
      default:
        return { bg: 'rgba(107, 114, 128, 0.2)', border: 'rgba(107, 114, 128, 0.3)', text: '#9ca3af', icon: 'circle' };
    }
  };

  return (
    <div className="eq-panel">
      <div className="eq-panel-header">
        <div className="eq-panel-selector" ref={dropdownRef}>
          <button className="eq-panel-selector-btn" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
            <span className="material-symbols-outlined eq-panel-icon">precision_manufacturing</span>
            <span className="eq-panel-name">{selectedMachine?.equipmentName || t('pages.monitoring.equipmentBoard.selectMachine')}</span>
            {selectedMachine && (
              <span className="eq-panel-model">{selectedMachine.manufacturer} · {selectedMachine.model}</span>
            )}
            <span className="material-symbols-outlined eq-panel-arrow">
              {isDropdownOpen ? 'expand_less' : 'expand_more'}
            </span>
          </button>
          {isDropdownOpen && (
            <div className="eq-panel-dropdown">
              {machines.map(m => (
                <div
                  key={m.id}
                  className={`eq-panel-dropdown-item ${selectedId === m.id ? 'active' : ''}`}
                  onClick={() => handleSelect(m.id)}
                >
                  <span className="eq-panel-dropdown-name">{m.equipmentName}</span>
                  <span className="eq-panel-dropdown-model">{m.manufacturer} · {m.model}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {loading && <span className="eq-panel-loading">{t('pages.monitoring.equipmentBoard.loading')}</span>}
        {lastUpdated && (
          <div className="eq-update-info" ref={updateInfoRef}>
            <div className="update-label-container">
              <span className="update-label">{t('pages.monitoring.equipmentBoard.update')}</span>
              <span className="material-symbols-outlined refresh-icon">sync</span>
            </div>
            <div className="update-time">
              {lastUpdated.toLocaleString('ko-KR')}
            </div>
          </div>
        )}
      </div>

      <div className="eq-panel-body">
        {!selectedId ? (
          <div className="eq-panel-empty">{t('pages.monitoring.equipmentBoard.empty')}</div>
        ) : (
          <div className="eq-panel-content">
            {/* 알람 이력 */}
            <div className="eq-alarm-section">
              <div className="eq-section-title">
                <span className="material-symbols-outlined">notifications</span>
                {t('pages.monitoring.equipmentBoard.alarmHistory')}
                {alarms.length > 0 && <span className="eq-alarm-count">{alarms.length}</span>}
              </div>
              <div className="eq-alarm-list">
                {alarms.length === 0 ? (
                  <div className="eq-alarm-empty">{t('pages.monitoring.equipmentBoard.alarmEmpty')}</div>
                ) : (
                  alarms.map((a, i) => {
                    const ss = getSeverityStyle(a.severity);
                    return (
                      <div
                        key={i}
                        className="eq-alarm-card"
                        style={{ backgroundColor: ss.bg, borderColor: ss.border }}
                      >
                        <div className="eq-alarm-card-row1">
                          <span className="material-symbols-outlined" style={{ fontSize: 16, color: ss.text }}>{ss.icon}</span>
                          <span className="eq-alarm-code">#{a.code}</span>
                          <span className="eq-alarm-value">{a.value || ''}</span>
                          {a.status === 'active' && (
                            <span className="eq-alarm-live">
                              <span className="eq-alarm-live-dot" />LIVE
                            </span>
                          )}
                        </div>
                        <div className="eq-alarm-msg" style={{ color: ss.text }}>{a.message}</div>
                        <div className="eq-alarm-footer">
                          <span className="eq-alarm-time">{t('pages.monitoring.equipmentBoard.occurredPrefix')}{fmtTime(a.time)}</span>
                          <span className="eq-alarm-status" style={{ color: a.status === 'active' ? '#f87171' : '#4ade80' }}>
                            {a.status === 'active' ? t('pages.monitoring.equipmentBoard.statusActive') : t('pages.monitoring.equipmentBoard.statusResolved')}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* 샷 데이터 테이블 */}
            <div className="eq-shot-section">
              <div className="eq-section-title">
                <span className="material-symbols-outlined">table_chart</span>
                {t('pages.monitoring.equipmentBoard.shotData')}
                <span className="eq-shot-count">{t('pages.monitoring.equipmentBoard.shotCountSuffix', { n: shotRows.length })}</span>
              </div>
              <div className="eq-shot-table-wrap">
                <table className="eq-shot-table">
                  <thead>
                    <tr>
                      {SHOT_COLUMNS.map(([key, labelKey]) => (
                        <th key={key}>{t(labelKey)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {shotRows.length === 0 ? (
                      <tr><td colSpan={SHOT_COLUMNS.length} className="eq-shot-empty">{t('pages.monitoring.equipmentBoard.noData')}</td></tr>
                    ) : (
                      shotRows.map((row, idx) => (
                        <tr
                          key={row.shotCount || idx}
                          className={newShotIds.has(row.shotCount) ? 'eq-new-shot' : ''}
                        >
                          {SHOT_COLUMNS.map(([key]) => (
                            <td key={key}>{key === 'shotTime' ? fmtTime(row[key]) : fmtVal(row[key])}</td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 패널 내부 알람 오버레이 */}
      {panelAlarms && panelAlarms.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '56px',
          right: '8px',
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          maxHeight: 'calc(100% - 64px)',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}>
          <style>{`
            @keyframes panelAlarmSlide {
              from { transform: translateX(100%); opacity: 0; }
              to { transform: translateX(0); opacity: 1; }
            }
            @keyframes panelAlarmPulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
          `}</style>
          {panelAlarms.map((alarm) => (
            <div key={alarm.id} style={{
              background: 'linear-gradient(135deg, rgba(26,26,46,0.95) 0%, rgba(22,33,62,0.95) 100%)',
              border: `2px solid ${alarm.severity === 'CRITICAL' ? '#EF4444' : '#F59E0B'}`,
              borderRadius: '4px',
              padding: '10px 14px',
              minWidth: splitCount === 1 ? '320px' : '240px',
              maxWidth: splitCount === 1 ? '360px' : '280px',
              boxShadow: `0 4px 16px ${alarm.severity === 'CRITICAL' ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)'}`,
              animation: 'panelAlarmSlide 0.3s ease-out',
              fontSize: splitCount >= 4 ? '11px' : '12px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '14px', marginRight: '6px', animation: 'panelAlarmPulse 1s infinite' }}>🚨</span>
                <span style={{ color: alarm.severity === 'CRITICAL' ? '#EF4444' : '#F59E0B', fontWeight: 'bold', fontSize: splitCount >= 4 ? '11px' : '13px' }}>{t('pages.monitoring.equipmentBoard.panelAlarm')}</span>
                <button
                  onClick={() => onRemovePanelAlarm && onRemovePanelAlarm(alarm.id)}
                  style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '14px' }}
                >✕</button>
              </div>
              <div style={{ color: '#F59E0B', fontWeight: 'bold', marginBottom: '4px' }}>{alarm.code}</div>
              <div style={{ color: '#aaa', fontSize: splitCount >= 4 ? '10px' : '11px' }}>{alarm.message || alarm.desc}</div>
              <div style={{ color: '#666', fontSize: '10px', marginTop: '4px' }}>{alarm.time}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EquipmentBoard() {
  const { t } = useTranslation();
  const [splitCount, setSplitCount] = useState(() => {
    const saved = localStorage.getItem('eq-board-split');
    return saved ? parseInt(saved) : 1;
  });
  const [machines, setMachines] = useState([]);
  const [alarmPopup, setAlarmPopup] = useState(null);
  const [panelAlarms, setPanelAlarms] = useState({});
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const updateInfoRef = useRef(null); // { panelIndex: [alarm, ...] }
  const panelSelectedIdsRef = useRef({});
  const { alarms: contextAlarms, removeAlarm: removeContextAlarm, updateEquipmentBoardIds } = useAlarmNotification();

  useEffect(() => {
    fetchEquipments()
      .then(data => { if (Array.isArray(data)) setMachines(data); })
      .catch(err => console.error('장비 목록 조회 실패:', err));
  }, []);

  const handleSplitChange = useCallback((count) => {
    setSplitCount(count);
    localStorage.setItem('eq-board-split', String(count));
  }, []);

  const handlePanelSelectedIdChange = useCallback((panelIndex, equipmentId) => {
    panelSelectedIdsRef.current[panelIndex] = equipmentId;
    const ids = Object.values(panelSelectedIdsRef.current).filter(Boolean);
    updateEquipmentBoardIds(ids);
    // 패널 선택 저장
    localStorage.setItem('eq-board-panels', JSON.stringify(panelSelectedIdsRef.current));
  }, [updateEquipmentBoardIds]);

  // 실제 알람(AlarmNotificationContext)을 패널별로 분배
  const prevContextAlarmsRef = useRef([]);
  useEffect(() => {
    if (contextAlarms.length > prevContextAlarmsRef.current.length) {
      const newOnes = contextAlarms.slice(0, contextAlarms.length - prevContextAlarmsRef.current.length);
      for (const alarm of newOnes) {
        // alarm.equipmentId로 어느 패널에 해당하는지 찾기
        const panelIndex = Object.entries(panelSelectedIdsRef.current)
          .find(([, eqId]) => eqId === alarm.equipmentId)?.[0];
        if (panelIndex !== undefined) {
          setPanelAlarms(prev => ({
            ...prev,
            [panelIndex]: [alarm, ...(prev[panelIndex] || [])],
          }));
        }
      }
    }
    prevContextAlarmsRef.current = contextAlarms;
  }, [contextAlarms]);

  const removePanelAlarm = useCallback((panelIndex, alarmId) => {
    setPanelAlarms(prev => ({
      ...prev,
      [panelIndex]: (prev[panelIndex] || []).filter(a => a.id !== alarmId),
    }));
    removeContextAlarm(alarmId);
  }, [removeContextAlarm]);

  const handleNewAlarm = useCallback((newAlarms, equipmentName) => {
    // active 상태인 알람만 팝업
    const activeAlarms = newAlarms.filter(a => a.status === 'active');
    if (activeAlarms.length > 0) {
      setAlarmPopup({ alarms: activeAlarms, equipmentName, currentIndex: 0 });
    }
  }, []);

  const dismissAlarmPopup = () => {
    if (!alarmPopup) return;
    const next = alarmPopup.currentIndex + 1;
    if (next < alarmPopup.alarms.length) {
      setAlarmPopup({ ...alarmPopup, currentIndex: next });
    } else {
      setAlarmPopup(null);
    }
  };

  const getSeverityStyle = (severity) => {
    switch (severity) {
      case 'CRITICAL':
        return { bg: 'rgba(239, 68, 68, 0.2)', border: 'rgba(239, 68, 68, 0.3)', text: '#f87171', icon: 'error' };
      case 'WARNING':
        return { bg: 'rgba(251, 191, 36, 0.2)', border: 'rgba(251, 191, 36, 0.3)', text: '#fbbf24', icon: 'warning' };
      case 'INFO':
        return { bg: 'rgba(59, 130, 246, 0.2)', border: 'rgba(59, 130, 246, 0.3)', text: '#60a5fa', icon: 'info' };
      default:
        return { bg: 'rgba(107, 114, 128, 0.2)', border: 'rgba(107, 114, 128, 0.3)', text: '#9ca3af', icon: 'circle' };
    }
  };

  const fmtTime = (t) => {
    if (!t) return '-';
    const idx = t.indexOf('T');
    if (idx < 0) return t;
    const date = t.substring(5, 10).replace('-', '/');
    const time = t.substring(idx + 1, idx + 9);
    return `${date} ${time}`;
  };

  return (
    <MainLayout>
      <div className="eq-board-container">
        <div className={`eq-board-grid split-${splitCount}`}>
          {Array.from({ length: splitCount }, (_, i) => (
            <EquipmentPanel
              key={i}
              index={i}
              machines={machines}
              splitCount={splitCount}
              onNewAlarm={handleNewAlarm}
              onSelectedIdChange={handlePanelSelectedIdChange}
              panelAlarms={panelAlarms[i] || []}
              onRemovePanelAlarm={(alarmId) => removePanelAlarm(i, alarmId)}
              onDataLoaded={() => {
                setLastUpdated(new Date());
                if (updateInfoRef.current) {
                  updateInfoRef.current.classList.add('refreshing');
                  setTimeout(() => updateInfoRef.current?.classList.remove('refreshing'), 1500);
                }
              }}
              lastUpdated={(splitCount === 1 && i === 0) || (splitCount > 1 && i === 1) ? lastUpdated : null}
              updateInfoRef={(splitCount === 1 && i === 0) || (splitCount > 1 && i === 1) ? updateInfoRef : null}
            />
          ))}
        </div>

        <div className="eq-fab-toolbar">
          {SPLIT_OPTIONS.map(opt => (
            <button
              key={opt.value}
              className={`eq-fab-btn ${splitCount === opt.value ? 'active' : ''}`}
              onClick={() => handleSplitChange(opt.value)}
              title={t(opt.labelKey)}
            >
              <span className="material-symbols-outlined">{opt.icon}</span>
            </button>
          ))}
        </div>

        {/* 새 알람 팝업 - 알람 카드 형태로 크게 표시 */}
        {alarmPopup && (() => {
          const a = alarmPopup.alarms[alarmPopup.currentIndex];
          const ss = getSeverityStyle(a.severity);
          const remaining = alarmPopup.alarms.length - alarmPopup.currentIndex - 1;
          return (
            <div className="eq-alarm-popup-overlay" onClick={dismissAlarmPopup}>
              <div className="eq-alarm-popup" onClick={e => e.stopPropagation()} style={{ '--alarm-bg': ss.bg, borderColor: ss.border }}>
                <div className="eq-alarm-popup-row1">
                  <span className="material-symbols-outlined eq-alarm-popup-icon" style={{ color: ss.text }}>{ss.icon}</span>
                  <span className="eq-alarm-popup-code" style={{ color: '#fff' }}>#{a.code}</span>
                  <span className="eq-alarm-popup-dash">-</span>
                  <span className="eq-alarm-popup-msg">{a.message}</span>
                  {remaining > 0 && <span className="eq-alarm-popup-remaining">{t('pages.monitoring.equipmentBoard.remainingSuffix', { n: remaining })}</span>}
                </div>
                <div className="eq-alarm-popup-row2">
                  <span className="eq-alarm-popup-time">{t('pages.monitoring.equipmentBoard.occurredPrefix')}{fmtTime(a.time)}</span>
                  <span className="eq-alarm-popup-status" style={{ color: a.status === 'active' ? '#f87171' : '#4ade80' }}>
                    {a.status === 'active' ? t('pages.monitoring.equipmentBoard.statusActive') : t('pages.monitoring.equipmentBoard.statusResolved')}
                  </span>
                </div>
                <button className="eq-alarm-popup-confirm" onClick={dismissAlarmPopup}>
                  {remaining > 0 ? t('pages.monitoring.equipmentBoard.confirmWithRemaining', { n: remaining }) : t('pages.monitoring.equipmentBoard.confirm')}
                </button>
              </div>
            </div>
          );
        })()}
      </div>
    </MainLayout>
  );
}

export default EquipmentBoard;
